/**
 * Pure TEA update for immediate chapter/act creation and the map include picker.
 *
 * Shell interprets create Cmds and pending scene-drop finish.
 */
import { assertNever } from "../../lib/assertNever";
import { isJust, just, nothing, type Maybe } from "../../lib/maybe";

export type OutlineCreateKind = "chapter" | "act";

export type OutlineCreateModel = {
  /** Outline-local act selection (does not change the editor active file). */
  readonly selectedActFolder: Maybe<string>;
  /** Picker to place an existing `.zwmap` into the manuscript sequence. */
  readonly includeMapOpen: boolean;
};

export const initialOutlineCreateModel: OutlineCreateModel = {
  selectedActFolder: nothing,
  includeMapOpen: false,
};

export type OutlineCreateMsg =
  | { readonly type: "Open"; readonly kind: OutlineCreateKind }
  | { readonly type: "Cancel" }
  | { readonly type: "ClearSelectedAct" }
  | { readonly type: "ToggleSelectedAct"; readonly folder: string }
  | { readonly type: "OpenIncludeMap" }
  | { readonly type: "CloseIncludeMap" }
  | { readonly type: "IncludeMap"; readonly mapFileName: string };

export type OutlineCreateCmd =
  | { readonly kind: "clearPendingDrop" }
  | { readonly kind: "createAct"; readonly name: string }
  | { readonly kind: "createChapter"; readonly name: string }
  | { readonly kind: "includeMap"; readonly mapFileName: string };

function closeCreate(model: OutlineCreateModel): OutlineCreateModel {
  return {
    ...initialOutlineCreateModel,
    selectedActFolder: model.selectedActFolder,
  };
}

export function updateOutlineCreate(
  model: OutlineCreateModel,
  msg: OutlineCreateMsg,
): readonly [OutlineCreateModel, readonly OutlineCreateCmd[]] {
  switch (msg.type) {
    case "Open":
      return [
        closeCreate(model),
        [
          {
            kind: msg.kind === "act" ? "createAct" : "createChapter",
            name: "",
          },
        ],
      ];
    case "Cancel":
      return [closeCreate(model), [{ kind: "clearPendingDrop" }]];
    case "ClearSelectedAct":
      return [{ ...model, selectedActFolder: nothing }, []];
    case "ToggleSelectedAct": {
      const selectedActFolder =
        isJust(model.selectedActFolder) &&
        model.selectedActFolder.value === msg.folder
          ? nothing
          : just(msg.folder);
      return [{ ...model, selectedActFolder }, []];
    }
    case "OpenIncludeMap":
      return [
        {
          ...closeCreate(model),
          selectedActFolder: model.selectedActFolder,
          includeMapOpen: true,
        },
        [],
      ];
    case "CloseIncludeMap":
      return [{ ...model, includeMapOpen: false }, []];
    case "IncludeMap": {
      const mapFileName = msg.mapFileName.trim();
      if (mapFileName.length === 0) {
        return [{ ...model, includeMapOpen: false }, []];
      }
      return [
        { ...model, includeMapOpen: false },
        [{ kind: "includeMap", mapFileName }],
      ];
    }
    default:
      return assertNever(msg);
  }
}
